/* eslint-disable @typescript-eslint/no-unused-vars */
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { delay, tap } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { IconKey } from '../../../../../whitelabel/icon-key.enum';
import { AuthService } from '../../../../core/services/auth.service';
import { LanguageService } from '../../../../core/services/language.service';
import { ButtonComponent } from '../../../../osp-ui/components/button/button/button.component';
import { TableCellData } from '../../../../osp-ui/components/data/table/contract/table-cell-data.contract';
import { TableCellBase } from '../../../../osp-ui/components/data/table/directives/table-cell-base.directive';
import { TABLE_CELL_DATA } from '../../../../osp-ui/components/data/table/table.constant';
import { MenuModel } from '../../../../osp-ui/components/menu/menu/contract/menu-model.contract';
import { MenuComponent } from '../../../../osp-ui/components/menu/menu/menu.component';
import { ConfirmationService } from '../../../../osp-ui/services/confirmation.service';
import { DeleteAlarmRuleCommand } from '../../../../pages/notifications/contract/delete-alarm-rule-command.contract';
import { ViewAlarmRule } from '../../../../pages/notifications/contract/view-alarm-rule.contract';
import { AlarmType } from '../../../../pages/notifications/enums/alarm-type.enum';
import { AlarmTypesService } from '../../../../pages/notifications/services/alarm-types.service';
import { NotificationsDatasourceService } from '../../../../pages/notifications/services/notifications-datasource.service';
import { NotificationFormDialogService } from '../../../../pages/notifications/services/notifications-form-dialog.service';
import { Authorities } from '../../../enums/authorities.enum';
import { Modules } from '../../../enums/modules.enum';
import { IconPipe } from '../../../pipes/icon.pipe';
import { ToastService } from '../../../services/toast.service';

type TableCellPayload = {
  onClick: (row: TableCellNotificationActionsComponent['row']) => void;
};

@Component({
  selector: 'osp-table-cell-notification-actions',
  standalone: true,
  imports: [ButtonComponent, MenuComponent, IconPipe],
  templateUrl: './table-cell-notification-actions.component.html',
  styleUrl: './table-cell-notification-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableCellNotificationActionsComponent extends TableCellBase implements OnInit {
  private destroyRef = inject(DestroyRef);

  @ViewChild('actionsMenu') menu!: MenuComponent;

  protected options: MenuModel = [];
  protected isUpdateDeleteAllowed =
    this.authService.hasAuthority(Authorities.NOTIFICATIONS_MANAGE_ALARMS) &&
    this.authService.hasModule(Modules.ALARMS);

  onClick: () => void = () => { };

  constructor(
    @Inject(TABLE_CELL_DATA) data: TableCellData<TableCellPayload>,
    private languageService: LanguageService,
    private confirmationService: ConfirmationService,
    private toastService: ToastService,
    private notificationsDatasource: NotificationsDatasourceService,
    protected notificationFormDialogService: NotificationFormDialogService,
    private authService: AuthService,
    private alarmTypesService: AlarmTypesService,
  ) {
    super(data);
    this.setComponentPayload(data);
  }

  ngOnInit(): void {
    this.alarmTypesService.allowedAlarmTypes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(types =>
        this.setMenuOptions((<ViewAlarmRule>this.row).alarmType)
      );
  }

  public static getComponentPayload(payloadOptions: TableCellPayload): TableCellPayload {
    return payloadOptions;
  }

  protected toggleMenu(event: Event): void {
    this.menu.toggle(event);
  }

  private setComponentPayload(data?: TableCellData<TableCellPayload>): void {
    this.onClick = (): void => data?.payload?.onClick(this.row);
  }

  private handleDeleteClick(): void {
    this.confirmationService.confirm({
      message: this.translate('modules.notification.notificationList.deleteAlarmRuleConfirmationMessage'),
      header: this.translate('modules.notification.notificationList.deleteAlarmRule'),
      accept: () => {
        this.deleteAlarmRule({
          alarmRuleId: this.row.alarmRuleId,
          alarmType: this.row.alarmType,
        });
      },
    });
  }

  private deleteAlarmRule(deleteAlarmRuleCommand: DeleteAlarmRuleCommand): void {
    this.notificationsDatasource
      .deleteAlarmRule(deleteAlarmRuleCommand)
      .pipe(
        delay(environment.DELAY_LONG),
        tap(() => this.notificationsDatasource.updateData()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.toastService.showSuccess({
            summary: this.translate('general.alarms.alarmRuleSuccesfullyDeleted'),
          });
        },
        error: (err) => {
          this.toastService.showError({
            summary: this.translate('general.alarms.couldNotDeleteAlarmRule'),
            detail: err,
          });
        },
      });
  }

  private setMenuOptions(type: AlarmType | undefined): void {
    if (!this.isUpdateDeleteAllowed || !type) {
      this.options = [];
      return;
    }

    const isEditDisabled = !this.alarmTypesService.isTypeAllowed(type);
    this.options = [
      {
        label: this.translate('general.buttons.edit'),
        command: () => {
          this.notificationFormDialogService.openDialog(this.row);
        },
        disabled: isEditDisabled,
        tooltip: isEditDisabled ? this.translate('modules.notification.notificationList.noRequiredModule') : '',
      },
      {
        label: this.translate('general.buttons.delete'),
        command: () => {
          this.handleDeleteClick();
        },
        styleClass: 'menu-item--danger',
      },
    ];
  }

  private translate(key: string, interpolateParams?: object): string {
    return this.languageService.getInstant(key, interpolateParams);
  }

  protected readonly IconKey = IconKey;
}
